import React, { useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Icon } from '../components/Icon';
import TelegramStar from '../components/TelegramStar';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { getCurrencySymbol } from '../utils/format';
import './PlansScreen.css';
import { useSwipeBack } from '../hooks/useSwipeBack';
import { useTranslation } from 'react-i18next';

interface Plan {
  id: string;
  duration: string;
  durationShort?: string;
  price: number;
  pricePerMonth?: number;
  discount?: string;
  popular?: boolean;
  priceId?: string;
  currency: string;
}

export const PlansScreen: React.FC = () => {
  const { t } = useTranslation();
  const { setCurrentPage, settings, setSettings } = useAppStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('3m');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState<string | null>(null);
  const [step, setStep] = useState<'currency' | 'plans'>('currency');

  // Загружаем настройки при открытии экрана
  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await apiClient.getSettings();
        const actualSettings = (response as any).settings || response;
        setSettings(actualSettings);
        console.log('Settings loaded in PlansScreen:', actualSettings);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, [setSettings]);

  const loadPrices = async (selectedCurrency: string) => {
    try {
      setLoading(true);
      setCurrency(selectedCurrency);
      
      const { plans: loadedPlans } = await apiClient.getSubscriptionPrices(selectedCurrency);
      
      // Добавляем вычисленные поля
      const enhancedPlans = loadedPlans.map((plan) => {
        let pricePerMonth, discount;
        
        if (plan.id === '1m') {
          pricePerMonth = plan.price;
          discount = undefined;
        } else if (plan.id === '3m') {
          pricePerMonth = plan.price / 3;
          discount = '-11%';
        } else if (plan.id === '6m') {
          pricePerMonth = plan.price / 6;
          discount = '-22%';
        } else if (plan.id === '12m') {
          pricePerMonth = plan.price / 12;
          discount = '-34%';
        }
        
        return {
          ...plan,
          pricePerMonth,
          discount,
          popular: plan.id === '3m',
          durationShort: plan.id
        };
      });
      
      setPlans(enhancedPlans);
      setCurrency(selectedCurrency);
      setStep('plans');
    } catch (error) {
      console.error('Error loading prices:', error);
      alert(t('plansScreen.loadPricesError'));
      setStep('currency');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCurrency = async (selectedCurrency: string) => {
    await loadPrices(selectedCurrency);
  };

  const currencySymbol = getCurrencySymbol(currency || 'RUB');

  const handleSubscribe = async () => {
    setIsSubmitting(true);
    
    try {
      // Получаем Telegram WebApp
      const tg = (window as any).Telegram?.WebApp;
      
      // Формируем код плана
      const planCode = selectedPlan === '12m' ? 'P12M' : selectedPlan === '6m' ? 'P6M' : selectedPlan === '3m' ? 'P3M' : 'P1M';
      
      // ===== TELEGRAM STARS =====
      if (currency === 'XTR' || currency === 'STARS') {
        // Проверяем, запущено ли приложение в Telegram
        const isInTelegram = tg && tg.initData;
        
        if (!isInTelegram) {
          // Пользователь НЕ в Telegram - создаём deep link
          console.log('🌐 User is NOT in Telegram, creating deep link for Stars payment');
          
          try {
            const deepLinkResponse = await apiClient.createTelegramPaymentDeepLink(planCode);
            
            console.log(`✅ Deep link created: ${deepLinkResponse.telegram_link}`);
            
            // Сохраняем токен для проверки статуса
            localStorage.setItem('telegram_payment_token', deepLinkResponse.payment_token);
            
            // Открываем Telegram через deep link
            // На мобильных устройствах используем прямой переход
            window.location.href = deepLinkResponse.telegram_link;
            
            return;
          } catch (error) {
            console.error('Error creating deep link:', error);
            throw new Error(t('plansScreen.deepLinkError'));
          }
        }
        
        // Если пользователь В Telegram - обычная логика с invoice
        if (!tg) {
          alert(t('plansScreen.openInTelegram'));
          return;
        }
        
        console.log(`Creating payment for ${planCode} in ${currency}`);
        
        // Создаем платеж через API
        const response: any = await apiClient.createPaymentLink(planCode, currency || 'RUB');
        
        // Проверяем тестовый режим
        if (response.test_mode) {
          alert(response.message || t('plansScreen.testModeSuccess'));
          setCurrentPage('subscription');
          return;
        }
        
        // Открываем invoice через Telegram WebApp API
        const invoiceLink = response.invoice_link;
        if (!invoiceLink) {
          throw new Error(t('plansScreen.paymentGenericError'));
        }
        
        console.log(`Opening Stars invoice: ${invoiceLink}`);
        
        // Открываем invoice и ждем результат
        tg.openInvoice(invoiceLink, (status: string) => {
          console.log(`Invoice status: ${status}`);
          if (status === 'paid') {
            alert(t('plansScreen.paymentSuccess'));
            setCurrentPage('subscription');
          } else if (status === 'cancelled') {
            alert(t('plansScreen.paymentCancelled'));
          } else if (status === 'failed') {
            alert(t('plansScreen.paymentFailed'));
          }
        });
        
        return;
      }
      
      // ===== STRIPE =====
      if (!tg) {
        alert(t('plansScreen.openInTelegram'));
        return;
      }

      
      console.log(`Creating payment for ${planCode} in ${currency}`);
      
      // Создаем платеж через API
      const response: any = await apiClient.createPaymentLink(planCode, currency || 'RUB');
      
      const payment_url = response.payment_url;
      
      if (!payment_url) {
        throw new Error(t('plansScreen.paymentGenericError'));
      }
      
      console.log(`Payment URL created: ${payment_url}`);
      
      // Открываем ссылку через Telegram WebApp API
      tg.openLink(payment_url);
      
    } catch (error: any) {
      console.error('Error creating payment:', error);
      
      // Проверяем специфичные ошибки
      if (error?.message?.includes('Email or phone required')) {
        const needEmail = confirm(
          `${t('plansScreen.emailRequiredGeneric')}\n\n${t('plansScreen.emailRequiredConfirm')}`
        );
        if (needEmail) {
          setCurrentPage('subscription');
        }
      } else if (error?.message?.includes('Stars payment not available')) {
        alert(t('plansScreen.starsComingSoon'));
      } else {
        alert(error?.message || t('plansScreen.paymentGenericError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Жест «назад»:
  useSwipeBack(containerRef, () => {
    if (step === 'currency') setCurrentPage('subscription');
    else setStep('currency');
  });

  // Шаг 1: Выбор валюты
  if (step === 'currency') {
    return (
      <div ref={containerRef} className="plans-screen">
        <Header title={t('plansScreen.currencyTitle')} onBack={() => setCurrentPage('subscription')} />
        <div className="plans-content">
          <div className="plans-hero">
            <div className="plans-hero__icon"><Icon name="credit_card" size={32} /></div>
            <h2 className="plans-hero__title">{t('plansScreen.currencyHeroTitle')}</h2>
            <p className="plans-hero__subtitle">
              {t('plansScreen.currencyHeroSubtitle')}
            </p>
          </div>

          <div className="currency-selector" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Card
              padding="large"
              className="currency-card"
              onClick={() => handleSelectCurrency('RUB')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer' }}>
                <div style={{ fontSize: '24px' }}><Icon name="flag" size={24} /></div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600' }}>
                    {t('plansScreen.currencyRUB')}
                  </h3>
                  <p style={{ margin: 0, fontSize: '14px', color: '#8E8E93' }}>
                    {t('plansScreen.currencyRUBDesc')}
                  </p>
                </div>
                <Icon name="chevron-right" size={24} color="#8E8E93" />
              </div>
            </Card>

            <Card
              padding="large"
              className="currency-card"
              onClick={() => handleSelectCurrency('USD')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer' }}>
                <div style={{ fontSize: '24px' }}><Icon name="globe" size={24} /></div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600' }}>
                    {t('plansScreen.currencyUSD')}
                  </h3>
                  <p style={{ margin: 0, fontSize: '14px', color: '#8E8E93' }}>
                    {t('plansScreen.currencyUSDDesc')}
                  </p>
                </div>
                <Icon name="chevron-right" size={24} color="#8E8E93" />
              </div>
            </Card>

            <Card
              padding="large"
              className="currency-card"
              onClick={() => handleSelectCurrency('XTR')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer' }}>
                <div style={{ fontSize: '24px' }}><TelegramStar size={32} animated /></div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {t('plansScreen.currencyStars')}
                  </h3>
                  <p style={{ margin: 0, fontSize: '14px', color: '#8E8E93' }}>
                    {t('plansScreen.currencyStarsDesc')}
                  </p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#34C759', fontWeight: '500' }}>
                    {(() => {
                      const userCurrency = settings?.currency_code || settings?.currency || 'RUB';
                      // Примерный курс: 1 Star ≈ $0.013 USD
                      const starToUSD = 0.013;
                      const rates: { [key: string]: number } = {
                        'RUB': 95,    // 1 USD ≈ 95 RUB
                        'EUR': 0.92,  // 1 USD ≈ 0.92 EUR
                        'GBP': 0.79,  // 1 USD ≈ 0.79 GBP
                        'KZT': 450,   // 1 USD ≈ 450 KZT
                        'UAH': 41,    // 1 USD ≈ 41 UAH
                        'USD': 1,
                      };
                      
                      const rate = rates[userCurrency] || 1;
                      const starInUserCurrency = starToUSD * rate;
                      const symbols: { [key: string]: string } = {
                        'RUB': '₽',
                        'USD': '$',
                        'EUR': '€',
                        'GBP': '£',
                        'KZT': '₸',
                        'UAH': '₴',
                      };
                      const symbol = symbols[userCurrency] || userCurrency;
                      
                      return t('plansScreen.starsRate', { rate: starInUserCurrency.toFixed(2), symbol });
                    })()}
                  </p>
                </div>
                <Icon name="chevron-right" size={24} color="#8E8E93" />
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="plans-screen">
        <Header title={t('plansScreen.plansTitle')} onBack={() => setStep('currency')} />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ color: '#8E8E93' }}>{t('plansScreen.loadingPrices')}</p>
        </div>
      </div>
    );
  }

  // Шаг 2: Выбор плана
  return (
    <div ref={containerRef} className="plans-screen">
      <Header title={t('plansScreen.plansTitle')} onBack={() => setStep('currency')} />

      <div className="plans-content">
        {/* Premium Badge */}
        <div className="plans-hero">
          <div className="plans-hero__icon"><Icon name="trophy" size={32} /></div>
          <h2 className="plans-hero__title">{t('plansScreen.premiumTitle')}</h2>
          <p className="plans-hero__subtitle">
            {t('plansScreen.premiumSubtitle')}
          </p>
        </div>

        {/* Features */}
        <Card padding="medium" className="features-card neo-static">
          <h3 className="plans-features__title">{t('plansScreen.featuresTitle')}</h3>
          <ul className="plans-features__list">
            <li className="plans-features__item">
              <Icon name="check" size={20} color="#34C759" />
              <span>{t('plansScreen.feature1')}</span>
            </li>
            <li className="plans-features__item">
              <Icon name="check" size={20} color="#34C759" />
              <span>{t('plansScreen.feature2')}</span>
            </li>
            <li className="plans-features__item">
              <Icon name="check" size={20} color="#34C759" />
              <span>{t('plansScreen.feature3')}</span>
            </li>
            <li className="plans-features__item">
              <Icon name="check" size={20} color="#34C759" />
              <span>{t('plansScreen.feature4')}</span>
            </li>
            <li className="plans-features__item">
              <Icon name="check" size={20} color="#34C759" />
              <span>{t('plansScreen.feature5')}</span>
            </li>
            <li className="plans-features__item">
              <Icon name="check" size={20} color="#34C759" />
              <span>{t('plansScreen.feature6')}</span>
            </li>
          </ul>
        </Card>

        {/* Plans Grid */}
        <div className="plans-grid">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              padding="medium"
              className={`plan-card ${selectedPlan === plan.id ? 'plan-card--selected' : ''} ${plan.popular ? 'plan-card--popular' : ''}`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <div className="plan-card__badge">{t('plansScreen.popularBadge')}</div>
              )}
              {plan.discount && (
                <div className="plan-card__discount">{plan.discount}</div>
              )}
              
              <div className="plan-card__duration">{plan.duration}</div>
              
              <div className="plan-card__price">
                <span className="plan-card__price-amount">{plan.price}</span>
                <span className="plan-card__price-currency">
                  {currency === 'XTR' || currency === 'STARS' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '4px' }}>
                      <TelegramStar size={24} />
                    </span>
                  ) : currencySymbol}
                </span>
              </div>
              
              {plan.pricePerMonth && (
                <>
                  <div className="plan-card__price-per-month">
                    {currency === 'XTR' || currency === 'STARS' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {Math.round(plan.pricePerMonth)} <TelegramStar size={16} />{t('plansScreen.perMonth')}
                      </span>
                    ) : (
                      <>
                        {plan.pricePerMonth.toFixed(2)} {currencySymbol}{t('plansScreen.perMonth')}
                      </>
                    )}
                  </div>
                  {(currency === 'XTR' || currency === 'STARS') && (
                    <div className="plan-card__price-fiat" style={{ 
                      fontSize: '12px', 
                      color: '#8E8E93', 
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {(() => {
                        const userCurrency = settings?.currency_code || settings?.currency || 'RUB';
                        const starToUSD = 0.013;
                        const rates: { [key: string]: number } = {
                          'RUB': 95,
                          'EUR': 0.92,
                          'GBP': 0.79,
                          'KZT': 450,
                          'UAH': 41,
                          'USD': 1,
                        };
                        
                        const rate = rates[userCurrency] || 1;
                        const starsPerMonth = plan.pricePerMonth;
                        const fiatPerMonth = starsPerMonth * starToUSD * rate;
                        const symbols: { [key: string]: string } = {
                          'RUB': '₽',
                          'USD': '$',
                          'EUR': '€',
                          'GBP': '£',
                          'KZT': '₸',
                          'UAH': '₴',
                        };
                        const symbol = symbols[userCurrency] || userCurrency;
                        
                        return `≈ ${fiatPerMonth.toFixed(2)} ${symbol}${t('plansScreen.perMonth')}`;
                      })()}
                    </div>
                  )}
                </>
              )}

              {selectedPlan === plan.id && (
                <div className="plan-card__check">
                  <Icon name="check" size={24} color="white" />
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Payment Info */}
        <div className="plans-info">
          {(currency === 'XTR' || currency === 'STARS') ? (
            <>
              <p className="plans-info__text">{t('plansScreen.infoStars1')}</p>
              <p className="plans-info__text">{t('plansScreen.infoStars2')}</p>
              <p className="plans-info__text">{t('plansScreen.infoStars3')}</p>
              <p className="plans-info__text">{t('plansScreen.infoStars4')}</p>
            </>
          ) : (
            <>
              <p className="plans-info__text">{t('plansScreen.infoStripe1')}</p>
              <p className="plans-info__text">{t('plansScreen.infoStripe2')}</p>
              <p className="plans-info__text">{t('plansScreen.infoStripe3')}</p>
              <p className="plans-info__text">{t('plansScreen.infoStripe4')}</p>
            </>
          )}
        </div>

        {/* Subscribe Button */}
        <button
          className="neo-raised"
          onClick={handleSubscribe}
          disabled={isSubmitting}
          style={{
            width: '100%',
            background: 'var(--neo-bg)',
            border: 'none',
            borderRadius: '14px',
            color: 'var(--color-primary)',
            fontWeight: '600',
            fontSize: '18px',
            padding: '16px',
            marginTop: '24px',
            cursor: 'pointer',
            fontFamily: 'inherit'
          }}
        >
          {isSubmitting ? t('plansScreen.submitting') : (
            currency === 'XTR' || currency === 'STARS' ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                {t('plansScreen.subscribeButtonStars', { price: plans.find(p => p.id === selectedPlan)?.price })} <TelegramStar size={20} />
              </span>
            ) : (
              t('plansScreen.subscribeButton', { price: `${plans.find(p => p.id === selectedPlan)?.price} ${currencySymbol}` })
            )
          )}
        </button>
      </div>
    </div>
  );
};

